import { useEffect, useState } from "react";

import { DEFAULT_PAGE_SIZE } from "../components/Pagination";

export function usePagination(initialLimit = DEFAULT_PAGE_SIZE) {
  const [limit, setLimit] = useState(initialLimit);
  const [offset, setOffset] = useState(0);
  return { limit, offset, setLimit, setOffset, reset: () => setOffset(0) };
}

/**
 * One page of a list that is already in memory. The offset snaps back when
 * the list shrinks under it (a filter, a refetch), so a page is never empty
 * while rows exist. `resetKey` starts over at page one when it changes, such
 * as a new sort or filter.
 */
export function useClientPage<T>(
  items: readonly T[],
  options: {
    /** Rows per page to start with. */
    initialLimit?: number;
    /** Rows per page from outside (a saved preference); wins over the page's own choice. */
    limit?: number;
    onLimitChange?: (limit: number) => void;
    resetKey?: unknown;
  } = {},
) {
  const [ownLimit, setOwnLimit] = useState(options.initialLimit ?? DEFAULT_PAGE_SIZE);
  const limit = options.limit ?? ownLimit;
  const [offset, setOffset] = useState(0);
  useEffect(() => setOffset(0), [options.resetKey]);
  const start = offset >= items.length ? Math.max(0, Math.floor((items.length - 1) / limit) * limit) : offset;
  return {
    pageItems: items.slice(start, start + limit),
    pager: {
      total: items.length,
      limit,
      offset: start,
      onLimitChange: (next: number) => {
        setOwnLimit(next);
        options.onLimitChange?.(next);
      },
      onOffsetChange: setOffset,
    },
  };
}
